/**
 * 自由变换工具（M2-14 / DESIGN.md §5.3）。
 *
 * 会话模型：Ctrl+T 进入 → 拖手柄改矩阵（预览走 2D canvas 的 GPU 双线性
 * drawImage，原图层在合成里隐藏）→ Enter 提交（wasm 烘焙，一条历史）
 * → Esc 取消（什么都没发生）。
 *
 * 手柄数学在 engine/transform/handles（纯函数、表驱动测试）；
 * 这里只做会话状态、指针路由与预览绘制。
 */
import type { Mat3, RasterLayer, Rect } from '../../document/types';
import { TILE, tileKey, type LayerId } from '../../engine/tile/key';
import {
  anchorFor,
  handlePoint,
  hitTestHandles,
  rotateByHandle,
  scaleByHandle,
  skewByHandle,
  type HandleId,
  type Point,
  type ScaleHandle,
} from '../../engine/transform/handles';
import { IDENTITY, aboutAnchor, applyTo, multiply, rotation, scaling } from '../../engine/transform/mat3';
import { bakeLayerTransform, transformedBounds } from '../../engine/transform/bake';
import type { AppStore } from '../../app/store';

/**
 * 约束模式（编辑→变换 子菜单，DESIGN.md §5.3 未列出扭曲/透视——那两个是
 * 真正的非仿射变形，Mat3 的 6 自由度装不下，得等专门的四角独立映射引擎，
 * 不在这次的范围里，菜单里如实置灰）。
 * - `free`：Ctrl+T 的默认行为，任意手柄都能缩放/旋转
 * - `scale`：只认 8 个缩放手柄，旋转热区不响应
 * - `rotate`：任意手柄命中都当旋转（锚点固定在中心，跟手柄具体是哪个无关）
 * - `skew`：只认 n/s/e/w 四条边，拖出错切而不是缩放
 */
export type TransformMode = 'free' | 'scale' | 'rotate' | 'skew';

interface Session {
  readonly layer: RasterLayer;
  /** 原始外接矩形（进入会话时的）。 */
  readonly bounds: Rect;
  readonly mode: TransformMode;
  /** 已定格的矩阵（此前的拖动合成）。 */
  committed: Mat3;
  /** 当前拖动的增量矩阵。 */
  live: Mat3;
  drag: {
    readonly handle: HandleId | 'move';
    readonly from: Point;
    readonly anchor: Point;
    /**
     * 按下那一刻的外接矩形（只含 committed，不含 live）——缩放/斜切手柄的
     * 换算基准必须整个拖拽过程锚定同一份，不能跟着每次 move 现算的
     * displayed（那份已经叠了上一次 move 产出的 live，一步比一步偏，
     * 表现出来就是画面抖、最终尺寸也跟鼠标实际拖动距离不一致）。
     */
    readonly referenceBounds: Rect;
  } | null;
  /** 会话开始时展平的源像素预览画布。 */
  readonly preview: HTMLCanvasElement | null;
}

export interface TransformDeps {
  readonly store: AppStore;
  readonly requestRender: () => void;
  readonly setHiddenLayer: (id: string | null) => void;
}

export class TransformToolController {
  readonly #deps: TransformDeps;
  #session: Session | null = null;
  /** 属性栏「等比」勾选框——开着时改宽/高两栏一起联动，关着时各改各的轴。 */
  #uniform = true;

  constructor(deps: TransformDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#session !== null;
  }

  /** 当前会话正在变换的图层 id；没有会话时 null。 */
  get layerId(): LayerId | null {
    return this.#session === null ? null : (this.#session.layer.id as LayerId);
  }

  get uniform(): boolean {
    return this.#uniform;
  }

  setUniform(value: boolean): void {
    this.#uniform = value;
  }

  /**
   * 属性栏实时显示的宽/高（px）——矩阵某一列的范数就是那根轴当前的缩放
   * 倍数，旋转矩阵的列范数恒为 1，所以这个算法在有旋转时依然准确，
   * 不是只在纯缩放时凑巧对。
   */
  get width(): number {
    const s = this.#session;
    if (s === null) return 0;
    const m = this.matrix;
    return Math.round(s.bounds.width * Math.hypot(m.a, m.b));
  }

  get height(): number {
    const s = this.#session;
    if (s === null) return 0;
    const m = this.matrix;
    return Math.round(s.bounds.height * Math.hypot(m.c, m.d));
  }

  /** 属性栏直接敲宽/高：按原内容左上角为锚点重新缩放（等比联动看 {@link uniform}）。 */
  applySizeOption(key: 'width' | 'height', value: number): void {
    const session = this.#session;
    if (session === null || !Number.isFinite(value) || value <= 0) return;
    const current = key === 'width' ? this.width : this.height;
    if (current <= 0) return;
    const factor = value / current;
    const sx = key === 'width' || this.#uniform ? factor : 1;
    const sy = key === 'height' || this.#uniform ? factor : 1;
    const anchor = { x: session.bounds.x, y: session.bounds.y };
    session.committed = multiply(aboutAnchor(scaling(sx, sy), anchor), session.committed);
    this.#deps.requestRender();
  }

  /** 当前显示矩阵（committed ∘ live）。 */
  get matrix(): Mat3 {
    const s = this.#session;
    return s === null ? IDENTITY : multiply(s.live, s.committed);
  }

  /**
   * Ctrl+T（或编辑→变换 的某个约束模式）：对最上层 raster 开始会话。
   * 没有可变换图层返回 false。已有会话在跑时忽略 mode——半路换约束
   * 没有意义，跟 Ctrl+T 命中已有会话时直接续用同一逻辑。
   */
  begin(mode: TransformMode = 'free'): boolean {
    if (this.#session !== null) return true;
    const document = this.#deps.store.document;
    if (document === null) return false;
    let layer: RasterLayer | null = null;
    for (let i = document.root.children.length - 1; i >= 0; i--) {
      const candidate = document.root.children[i]!;
      if (candidate.kind === 'raster') {
        layer = candidate;
        break;
      }
    }
    if (layer === null) return false;
    const bounds = this.#contentBounds(layer);
    if (bounds.width === 0 || bounds.height === 0) return false;

    this.#session = {
      layer,
      bounds,
      mode,
      committed: IDENTITY,
      live: IDENTITY,
      drag: null,
      preview: this.#flattenPreview(layer, bounds),
    };
    this.#deps.setHiddenLayer(layer.id);
    this.#deps.requestRender();
    return true;
  }

  /**
   * 编辑→变换 的离散操作（旋转 90°/180°、水平/垂直翻转）：直接绕内容
   * 中心应用一个定死的矩阵并立即提交，不需要用户拖手柄——复用
   * begin/commit 的整条链路（找图层、烘焙、历史、失效），不是另开一套。
   */
  async #quickTransform(matrix: Mat3, label: string): Promise<boolean> {
    if (this.#session === null && !this.begin()) return false;
    const session = this.#session!;
    const anchor = {
      x: session.bounds.x + session.bounds.width / 2,
      y: session.bounds.y + session.bounds.height / 2,
    };
    session.committed = multiply(aboutAnchor(matrix, anchor), session.committed);
    return await this.commit({ label });
  }

  async rotateQuick(direction: 'cw' | 'ccw'): Promise<boolean> {
    return await this.#quickTransform(
      rotation(direction === 'cw' ? Math.PI / 2 : -Math.PI / 2),
      direction === 'cw' ? '顺时针旋转 90°' : '逆时针旋转 90°',
    );
  }

  async rotate180Quick(): Promise<boolean> {
    return await this.#quickTransform(rotation(Math.PI), '旋转 180°');
  }

  async flipQuick(axis: 'horizontal' | 'vertical'): Promise<boolean> {
    return await this.#quickTransform(
      axis === 'horizontal' ? scaling(-1, 1) : scaling(1, -1),
      axis === 'horizontal' ? '水平翻转' : '垂直翻转',
    );
  }

  /** Esc：丢弃会话。 */
  cancel(): void {
    if (this.#session === null) return;
    this.#session = null;
    this.#deps.setHiddenLayer(null);
    this.#deps.requestRender();
  }

  /** Enter（或离散变换操作内部）：烘焙提交（一条历史）。 */
  async commit(options: { signal?: AbortSignal; label?: string } = {}): Promise<boolean> {
    const session = this.#session;
    const store = this.#deps.store;
    if (session === null) return false;
    const matrix = this.matrix;
    const document = store.document;
    if (document === null) return false;

    // 恒等提交 = 无操作退出
    const isIdentity =
      Math.abs(matrix.a - 1) < 1e-9 &&
      Math.abs(matrix.d - 1) < 1e-9 &&
      Math.abs(matrix.b) < 1e-9 &&
      Math.abs(matrix.c) < 1e-9 &&
      Math.abs(matrix.e) < 1e-9 &&
      Math.abs(matrix.f) < 1e-9;
    if (isIdentity) {
      this.cancel();
      return true;
    }

    const label = options.label ?? '自由变换';
    const transaction = store.beginPixelTransaction(label);
    const result = await bakeLayerTransform(
      store.tiles,
      session.layer.id as LayerId,
      session.bounds,
      matrix,
      document.size.width,
      document.size.height,
      transaction,
      options,
    );
    store.commitPixels(label, transaction.end());
    store.invalidatePixels([...result.dirty]);

    this.#session = null;
    this.#deps.setHiddenLayer(null);
    this.#deps.requestRender();
    return true;
  }

  /**
   * 指针路由。会话激活时接管一切画布指针。
   * tolerance 是文档像素（调用方按视口缩放换算，保证手感恒定）。
   */
  handlePointer(
    phase: 'down' | 'move' | 'up' | 'double',
    point: Point,
    tolerance: number,
  ): boolean {
    const session = this.#session;
    if (session === null) return false;

    if (phase === 'down') {
      const displayed = this.#displayedBounds();
      const rawHandle = hitTestHandles(displayed, point, tolerance);
      const handle = this.#constrainHandle(rawHandle, session.mode);
      const inside =
        point.x >= displayed.x &&
        point.x <= displayed.x + displayed.width &&
        point.y >= displayed.y &&
        point.y <= displayed.y + displayed.height;
      if (handle === null && !inside) return true; // 点在空处：吞掉但不动
      const which: HandleId | 'move' = handle ?? 'move';
      const anchor =
        which === 'rotate' || which === 'move'
          ? {
              x: displayed.x + displayed.width / 2,
              y: displayed.y + displayed.height / 2,
            }
          : anchorFor(displayed, which);
      session.drag = { handle: which, from: point, anchor, referenceBounds: displayed };
      return true;
    }

    if (phase === 'move' && session.drag !== null) {
      const { handle, from, anchor, referenceBounds } = session.drag;
      if (handle === 'move') {
        session.live = { a: 1, b: 0, c: 0, d: 1, e: point.x - from.x, f: point.y - from.y };
      } else if (handle === 'rotate') {
        session.live = rotateByHandle(anchor, from, point);
      } else if (session.mode === 'skew') {
        session.live = skewByHandle(referenceBounds, handle as 'n' | 's' | 'e' | 'w', from, point);
      } else {
        session.live = scaleByHandle(referenceBounds, handle as ScaleHandle, point, {
          uniform: this.#uniform,
        });
      }
      this.#deps.requestRender();
      return true;
    }

    if (phase === 'up' && session.drag !== null) {
      // 定格：live 并进 committed
      session.committed = multiply(session.live, session.committed);
      session.live = IDENTITY;
      session.drag = null;
      this.#deps.requestRender();
      return true;
    }
    return this.#session !== null;
  }

  /**
   * 约束模式下把命中的手柄重新解释成该模式允许的操作，见 {@link TransformMode}。
   * 返回 null 表示这个手柄在当前模式下不该响应（外层按"点在空处"处理）。
   */
  #constrainHandle(handle: HandleId | null, mode: TransformMode): HandleId | null {
    if (handle === null) return null;
    switch (mode) {
      case 'free':
        return handle;
      case 'scale':
        return handle === 'rotate' ? null : handle;
      case 'rotate':
        // 约束旋转模式下，命中任何手柄（甚至角/边）都当旋转——
        // 跟自由模式里只有"手柄圈外"才旋转不同，这里不需要区分具体是哪个手柄
        return 'rotate';
      case 'skew':
        return handle === 'n' || handle === 's' || handle === 'e' || handle === 'w' ? handle : null;
    }
  }

  /** 拖竖直/水平边 + 斜切修饰（供以后接 Ctrl 拖边）。 */
  applySkew(handle: 'n' | 's' | 'e' | 'w', from: Point, to: Point): void {
    const session = this.#session;
    if (session === null) return;
    session.committed = multiply(
      skewByHandle(this.#displayedBounds(), handle, from, to),
      session.committed,
    );
    this.#deps.requestRender();
  }

  /** 文档空间叠加：变换后的预览 + 边框 + 手柄。 */
  drawOverlay(ctx: CanvasRenderingContext2D, viewportScale: number): void {
    const session = this.#session;
    if (session === null) return;
    const m = this.matrix;

    if (session.preview !== null) {
      ctx.save();
      ctx.transform(m.a, m.b, m.c, m.d, m.e, m.f);
      ctx.imageSmoothingEnabled = true; // 预览：GPU 双线性
      ctx.drawImage(session.preview, session.bounds.x, session.bounds.y);
      ctx.restore();
    }

    // 边框与手柄画在变换后的角点上
    const b = session.bounds;
    const corners = [
      applyTo(m, { x: b.x, y: b.y }),
      applyTo(m, { x: b.x + b.width, y: b.y }),
      applyTo(m, { x: b.x + b.width, y: b.y + b.height }),
      applyTo(m, { x: b.x, y: b.y + b.height }),
    ];
    ctx.save();
    ctx.strokeStyle = 'rgba(64,128,255,0.9)';
    ctx.lineWidth = 1 / viewportScale;
    ctx.beginPath();
    ctx.moveTo(corners[0]!.x, corners[0]!.y);
    for (const c of corners.slice(1)) ctx.lineTo(c.x, c.y);
    ctx.closePath();
    ctx.stroke();
    const size = 6 / viewportScale;
    ctx.fillStyle = 'white';
    const displayed = this.#displayedBounds();
    // 约束模式下只画用户实际能拖的那几个手柄——旋转模式满盘都能转，
    // 画出一圈具体手柄反而暗示"只能从这几点转"，不画更准确
    const visibleHandles: readonly ScaleHandle[] =
      session.mode === 'rotate'
        ? []
        : session.mode === 'skew'
          ? (['n', 'e', 's', 'w'] as const)
          : (['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const);
    for (const handle of visibleHandles) {
      const p = handlePoint(displayed, handle);
      ctx.strokeRect(p.x - size / 2, p.y - size / 2, size, size);
      ctx.fillRect(p.x - size / 2, p.y - size / 2, size, size);
    }
    ctx.restore();
  }

  /** 当前显示的外接矩形（原 bounds 经 committed∘live）。 */
  #displayedBounds(): Rect {
    const session = this.#session!;
    const document = this.#deps.store.document!;
    return transformedBounds(
      session.bounds,
      this.matrix,
      document.size.width,
      document.size.height,
    );
  }

  /**
   * 内容的紧外接矩形——按实际非透明像素扫，不是驻留块的整块范围。
   *
   * 图层 bounds 字段可能过期（画笔不更新它，见旧注释），但驻留块粒度
   * （256×256）本身也不能直接当作外接矩形：一张拖放置入的小图片（比如
   * 80×80）经常整个落在一个块里，块粒度的"外接矩形"会比图片本身大好几倍
   * ——选框浮在图片周围一大圈空气里，用户抓的手柄跟图片边缘对不上，
   * 拖出来的结果也就跟直觉不符。逐像素扫 alpha 通道找真正的紧边界，
   * 一次性开销（进入会话时算一次，不是每帧），对目前的文档规模可接受。
   */
  #contentBounds(layer: RasterLayer): Rect {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const tile of this.#deps.store.tiles.residentTilesOf(layer.id as LayerId, 0)) {
      const data = tile.data;
      if (data === null) continue;
      const originX = tile.key.tx * TILE;
      const originY = tile.key.ty * TILE;
      for (let ly = 0; ly < TILE; ly++) {
        const rowBase = ly * TILE * 4;
        for (let lx = 0; lx < TILE; lx++) {
          if (data[rowBase + lx * 4 + 3] === 0) continue; // 全透明像素不算内容
          const x = originX + lx;
          const y = originY + ly;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x + 1 > maxX) maxX = x + 1;
          if (y + 1 > maxY) maxY = y + 1;
        }
      }
    }
    if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
    const document = this.#deps.store.document!;
    return {
      x: Math.max(0, minX),
      y: Math.max(0, minY),
      width: Math.min(document.size.width, maxX) - Math.max(0, minX),
      height: Math.min(document.size.height, maxY) - Math.max(0, minY),
    };
  }

  #flattenPreview(layer: RasterLayer, bounds: Rect): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    canvas.width = bounds.width;
    canvas.height = bounds.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return null;
    const image = ctx.createImageData(bounds.width, bounds.height);
    const store = this.#deps.store.tiles;
    for (let y = 0; y < bounds.height; y++) {
      const docY = bounds.y + y;
      for (let x = 0; x < bounds.width; x++) {
        const docX = bounds.x + x;
        const tile = store.get(
          tileKey(layer.id as LayerId, Math.floor(docX / TILE), Math.floor(docY / TILE)),
        );
        const data = tile?.data;
        if (data == null) continue;
        const from = ((docY % TILE) * TILE + (docX % TILE)) * 4;
        const to = (y * bounds.width + x) * 4;
        image.data[to] = data[from]!;
        image.data[to + 1] = data[from + 1]!;
        image.data[to + 2] = data[from + 2]!;
        image.data[to + 3] = data[from + 3]!;
      }
    }
    ctx.putImageData(image, 0, 0);
    return canvas;
  }
}
